import {FC} from "react";
import {Drawer, drawerClasses, Box} from "@mui/material";
import {styled} from "@mui/material/styles";
import Logo from "./Logo";
import MenuContent from "./MenuContent";
import MenuPanel from "./MenuPanel";
import {t} from "../../../styles/theme/tokens";
import {SIDEBAR_WIDTH} from "./constants";

/**
 * ============================================================================
 * SideMenu — постоянный боковой drawer на десктопе.
 * ============================================================================
 *
 * Структура (сверху вниз):
 *   ┌──────────────────────┐
 *   │ Logo (56px)          │  ← shrink-fixed, не скроллится
 *   ├──────────────────────┤
 *   │ MenuContent          │  ← flex:1, скроллится
 *   │ (группы пунктов)     │     при переполнении (.scroll-area)
 *   │                      │
 *   ├──────────────────────┤
 *   │ MenuPanel (48px)     │  ← shrink-fixed, не скроллится
 *   └──────────────────────┘
 *
 * Что УБРАНО из старой версии:
 *   - mt: 10 на корневом Drawer — был хаком, сдвигал весь sidebar на 80px
 *     вниз непонятно зачем. На скриншоте это создавало "плавание" логотипа.
 *   - <Divider/> между секциями — заменены на border на самих компонентах
 *     (Logo borderBottom, MenuPanel borderTop) для меньшей фрагментации.
 *   - Закомментированный <UserAvatar/> и mt:'calc(...template-frame-height)' —
 *     артефакты старого MUI Template, не нужны.
 *
 * Что СДЕЛАНО лучше:
 *   - Внутренний layout — flex column с явными секциями. Раньше всё было
 *     "Logo + Divider + Box{overflow:auto, height:100%} + Divider + MenuPanel"
 *     что не давало внятной семантики и Box раздувался на всё.
 *   - Сейчас: явный flex column, шапка/подвал — shrink:0, middle — flex:1.
 *
 * Скролл:
 *   - Класс .scroll-area из global.css даёт тонкий нейтральный scrollbar.
 *   - В обычной ситуации (нет переполнения) scrollbar просто не появляется.
 * ============================================================================
 */

const StyledDrawer = styled(Drawer)({
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    boxSizing: "border-box",

    [`& .${drawerClasses.paper}`]: {
        width: SIDEBAR_WIDTH,
        boxSizing: "border-box",
        backgroundColor: t.bg.paper,
        backgroundImage: "none",
        borderRight: `1px solid ${t.divider}`,
        display: "flex",
        flexDirection: "column",
    },
});

const ScrollableContent = styled(Box)({
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
});

const SideMenu: FC = () => {
    return (
        <StyledDrawer
            variant="permanent"
            sx={{display: {xs: "none", md: "block"}}}
        >
            <Logo/>
            <ScrollableContent className="scroll-area">
                <MenuContent/>
            </ScrollableContent>
            <MenuPanel/>
        </StyledDrawer>
    );
};

export default SideMenu;